import { supportAnalyticsApi } from './configUtils';
import { FI_TYPES } from '../../../Utils/accountUtils';
import { AccountDetails, FIStatus, FIType } from '../../../Utils/types';
import { ReactComponent as RightArrow } from '../../../Assets/CommonTenant/rightCircle-arrow.svg';
import { LoadingOutlined } from '@ant-design/icons';
import {
  AssetCategory,
  DashboardAssetType,
  FAILED_STATUSES,
} from './constants';
import { EVENTCODES, pushEventToParent } from '../../../Utils/eventUtils';
import { AASTEPS } from '../../AA';
import { message } from 'antd';
import { FlowType } from '../../../Utils/constants';
import { ROUTES } from '../../../Utils/routes';
import { NavigateFunction } from 'react-router-dom';

type allowToNavigateToAssetDetailType = {
  type: string;
  allowed: boolean;
  message: string;
  cta: string;
  icon: React.ComponentType<any>;
};

type handleOnAssetClickParams = {
  asset: AssetCategory;
  statusData: any;
  setToast: React.Dispatch<React.SetStateAction<string>>;
  isBackEventSent: any;
  linkedAccounts: number;
  events: {
    onAssetClickEvent: (typeof EVENTCODES)[keyof typeof EVENTCODES];
    clickToLinkAssetEvent: (typeof EVENTCODES)[keyof typeof EVENTCODES];
  };
  setPreFetchedLoadingForAssets: React.Dispatch<
    React.SetStateAction<AssetCategory | undefined>
  >;
  startAdditionalAccountJourney: (journeyType: FlowType) => void;
  flowTypeAsset: FlowType;
  setIsAdditionalAccountJourney: React.Dispatch<React.SetStateAction<boolean>>;
  setAAInternalStep: React.Dispatch<React.SetStateAction<AASTEPS | null>>;
  route: (typeof ROUTES)[keyof typeof ROUTES];
  setCurrentRoute: React.Dispatch<any>;
  navigate: NavigateFunction;
};

type linkNewAccountType = {
  asset: AssetCategory;
  setPreFetchedLoadingForAssets: React.Dispatch<
    React.SetStateAction<AssetCategory | undefined>
  >;
  startAdditionalAccountJourney: (journeyType: FlowType) => void;
  flowTypeAsset: FlowType;
  setIsAdditionalAccountJourney: React.Dispatch<React.SetStateAction<boolean>>;
  setAAInternalStep: React.Dispatch<React.SetStateAction<AASTEPS | null>>;
  events: (typeof EVENTCODES)[keyof typeof EVENTCODES];
};

const assetAccountsMap: Record<DashboardAssetType, FIType[]> = {
  [DashboardAssetType.MUTUAL_FUNDS]: [FIType.MUTUAL_FUNDS],
  [DashboardAssetType.BANKS]: [FIType.DEPOSIT],
  [DashboardAssetType.STOCKS]: [FIType.EQUITIES, FIType.ETF],
  [DashboardAssetType.EPFO]: [],
  [DashboardAssetType.NPS]: [FIType.NPS],
  [DashboardAssetType.FD]: [FIType.TERM_DEPOSIT, FIType.RECURRING_DEPOSIT],
};

export const allowToNavigateToAssetDetail = (
  accounts: any[]
): allowToNavigateToAssetDetailType => {
  const allRevoking = accounts.every(
    (account: any) => account.status === 'REVOKE_INITIATED'
  );
  if (allRevoking) {
    return {
      type: 'REVOKE',
      allowed: false,
      message: 'Account removal is in progress.',
      cta: 'Revoking consent',
      icon: LoadingOutlined,
    };
  }

  const allowed = accounts.some(
    (account: any) =>
      account.status === 'ACTIVE' && account.lastRawDataId != null
  );

  if (allowed) {
    return {
      type: 'DELIVERED',
      allowed: true,
      message: '',
      cta: '',
      icon: RightArrow,
    };
  }

  const hasRawData = accounts.some(
    (account: any) => account.lastRawDataId != null
  );

  if (!hasRawData) {
    return {
      type: 'NOTLINKED',
      allowed: true,
      message: 'No data available yet.',
      cta: 'Link Now',
      icon: RightArrow,
    };
  }

  const allInactive = accounts.every(
    (account: any) => account.status === 'INACTIVE'
  );

  if (allInactive) {
    return {
      type: 'INACTIVE',
      allowed: true,
      message: 'All accounts are inactive.',
      cta: 'Link Now',
      icon: RightArrow,
    };
  }

  const hasRevoking = accounts.some(
    (account: any) => account.status === 'REVOKE_INITIATED'
  );

  const hasInactive = accounts.some(
    (account: any) => account.status === 'INACTIVE'
  );

  if (hasRevoking && hasInactive) {
    return {
      type: 'INACTIVE_AND_REVOKING',
      allowed: false,
      message: 'Accounts are inactive or being removed.',
      cta: 'Processing your request',
      icon: LoadingOutlined,
    };
  }

  return {
    type: 'UNKNOWN_ERROR',
    allowed: true,
    message: 'Data is not available. Try again after sometime',
    cta: 'Link Now',
    icon: RightArrow,
  };
};

export const getAssetNavigationState = (
  asset: AssetCategory,
  statusData: any
) => {
  const allAccounts = statusData?.current?.accountDetails ?? [];
  const fiTypes = assetAccountsMap[asset];
  if (!fiTypes) {
    return {
      type: 'UNSUPPORTED_FITYPE',
      allowed: false,
      message: 'Unsupported asset type',
      cta: '',
      icon: RightArrow,
    };
  }

  const accounts = allAccounts.filter((acc: any) =>
    fiTypes.includes(acc.fiType)
  );

  if (accounts.length === 0) {
    return {
      type: 'NOTLINKED',
      allowed: true,
      message: '',
      cta: 'Link Now',
      icon: RightArrow,
    };
  }
  return allowToNavigateToAssetDetail(accounts);
};

export const checkAccountRevokeProgress = (
  statusData: any,
  asset: AssetCategory
) => {
  const allAccounts = statusData?.current?.accountDetails ?? [];
  const fiTypes = assetAccountsMap[asset];
  const accounts = allAccounts.filter((acc: any) =>
    fiTypes.includes(acc.fiType)
  );
  const checkRevoking = accounts.filter(
    (account: any) => account.status === 'REVOKE_INITIATED'
  );
  const allActive = accounts.filter(
    (account: any) => account.status === 'ACTIVE'
  );
  const revokingLinkRef = new Set(
    checkRevoking.map((account: any) => account.linkRefNumber)
  );
  allActive.forEach((account: any) => {
    if (revokingLinkRef.has(account.linkRefNumber)) {
      revokingLinkRef.delete(account.linkRefNumber);
    }
  });

  return revokingLinkRef.size > 0;
};

export const getAssetLinkedAccounts = (
  statusData: any,
  asset: AssetCategory,
  status: string
) => {
  const allAccounts = statusData?.current?.accountDetails ?? [];
  const fiTypes = assetAccountsMap[asset];

  const filteredAccounts = allAccounts.filter(
    (acc: any) => fiTypes.includes(acc.fiType) && acc.status === status
  );
  // if (status === 'ACTIVE') {
  //   return filteredAccounts.filter(
  //     (acc: any) => !FAILED_STATUSES.includes(acc.lastRequestFIStatus)
  //   );
  // }
  return filteredAccounts;
};

export const doesFiTypeExist = (category: AssetCategory, statusData: any) => {
  if (!statusData?.current?.accountDetails) return false;

  return statusData.current.accountDetails
    .filter((account: AccountDetails) => account.status === 'ACTIVE')
    .some((account: AccountDetails) =>
      isAccountOfCategory(account.fiType, category)
    );
};

export const isDataPending = (category: AssetCategory, statusData: any) => {
  if (!statusData?.current?.accountDetails) return false;
  const accountsForType = statusData.current.accountDetails
    .filter((account: AccountDetails) => {
      return account.status !== 'INACTIVE';
    })
    .filter((account: AccountDetails) =>
      isAccountOfCategory(account.fiType, category)
    );
  const supportAnalytics = supportAnalyticsApi();
  if (supportAnalytics) return isAnalyticsDataPending(accountsForType);
  return isRawDataPending(accountsForType);
};

export const TERMINAL_FI_FAILURE_STATUSES = [
  FIStatus.DATA_NOT_RETURNED_IN_TIME,
  FIStatus.TIMEOUT,
  FIStatus.DENIED,
  FIStatus.INVALID_DATA,
];

const isAnalyticsDataPending = (accountsForType: any) => {
  const stillPending = accountsForType.filter(
    (account: AccountDetails) =>
      !TERMINAL_FI_FAILURE_STATUSES.includes(account.lastRequestFIStatus)
  );
  return (
    stillPending.length > 0 &&
    stillPending.every(
      (account: AccountDetails) =>
        account.lastAnalyzedAt === null || account.lastAnalyzedAt === undefined
    )
  );
};

const isRawDataPending = (accountsForType: any) => {
  const stillPending = accountsForType.filter(
    (account: AccountDetails) =>
      !TERMINAL_FI_FAILURE_STATUSES.includes(account.lastRequestFIStatus)
  );
  return (
    stillPending.length > 0 &&
    stillPending.every(
      (account: AccountDetails) =>
        account.lastRawDataFetchedAt === null ||
        account.lastRawDataFetchedAt === undefined
    )
  );
};

export const isAccountOfCategory = (
  fiType: string,
  category: AssetCategory
): boolean => {
  switch (category) {
    case AssetCategory.BANKS:
      return fiType === FI_TYPES.DEPOSIT;
    case AssetCategory.MUTUAL_FUNDS:
      return fiType === FI_TYPES.MUTUAL_FUNDS;
    case AssetCategory.STOCKS:
      return fiType === FI_TYPES.EQUITIES || fiType === FI_TYPES.ETF;
    case AssetCategory.FIXED_DEPOSIT:
      return (
        fiType === FI_TYPES.TERM_DEPOSIT ||
        fiType === FI_TYPES.TERM_DEPOSIT_HYPHEN ||
        fiType === FI_TYPES.RECURRING_DEPOSIT
      );
    case AssetCategory.NPS:
      return fiType === FI_TYPES.NPS;
    default:
      return false;
  }
};

export const handleOnAssetClick = ({
  asset,
  statusData,
  setToast,
  isBackEventSent,
  linkedAccounts,
  events,
  setPreFetchedLoadingForAssets,
  startAdditionalAccountJourney,
  flowTypeAsset,
  setIsAdditionalAccountJourney,
  setAAInternalStep,
  route,
  setCurrentRoute,
  navigate,
}: handleOnAssetClickParams) => {
  const assetAccessInfo = getAssetNavigationState(asset, statusData);
  const fiTypeExist = doesFiTypeExist(asset, statusData);
  const dataPendingState = isDataPending(asset, statusData);
  if (fiTypeExist && dataPendingState) {
    setToast('Analysing your data');
    return;
  }
  if (!assetAccessInfo.allowed) {
    setToast(assetAccessInfo.message || 'Analysing your data');
    return;
  }
  isBackEventSent.current = true;
  pushEventToParent({ eventCode: events.onAssetClickEvent });

  if (
    linkedAccounts === 0 ||
    assetAccessInfo.type === 'NOTLINKED' ||
    assetAccessInfo.type === 'INACTIVE'
  ) {
    linkNewAccount({
      asset,
      setPreFetchedLoadingForAssets,
      startAdditionalAccountJourney,
      flowTypeAsset,
      setIsAdditionalAccountJourney,
      setAAInternalStep,
      events: events.clickToLinkAssetEvent,
    });
    return;
  }
  if (!dataPendingState && assetAccessInfo.type === 'DELIVERED') {
    navigate(route, {
      state: { fromAssetsDashboard: true },
    });
    setCurrentRoute(route);
    return;
  }
  message.info("Hang tight! We're getting things ready for you.");
};

export const linkNewAccount = ({
  asset,
  setPreFetchedLoadingForAssets,
  startAdditionalAccountJourney,
  flowTypeAsset,
  setIsAdditionalAccountJourney,
  setAAInternalStep,
  events,
}: linkNewAccountType) => {
  setPreFetchedLoadingForAssets(asset);
  startAdditionalAccountJourney(flowTypeAsset);
  if (asset === AssetCategory.BANKS) {
    setIsAdditionalAccountJourney(false);
    setAAInternalStep(AASTEPS.AA_JOURNEY);
  }
  pushEventToParent({ eventCode: events });
};

export const checkAssetPendingAccounts = (
  statusData: any,
  type: (typeof FI_TYPES)[keyof typeof FI_TYPES]
) => {
  return statusData.current.accountDetails.filter(
    (accountDetail: any) =>
      accountDetail.fiType === type &&
      accountDetail.lastRawDataFetchedAt === null &&
      accountDetail.status === 'ACTIVE'
  );
};

export const filteredLoadingAndFailedAccounts = (account: any[]) => {
  const failed: any[] = [];
  const loading: any[] = [];
  account.forEach((account) => {
    if (FAILED_STATUSES.includes(account.lastRequestFIStatus)) {
      failed.push(account);
    } else {
      loading.push(account);
    }
  });
  return { failed, loading };
};
